// src/pages/Appointment.jsx
import React from 'react';
import Layout from '../components/layout/Layout';
import AppointmentForm from '../components/forms/AppointmentForm';
import { motion } from 'framer-motion';
import { Clock, AlertCircle, Phone } from 'lucide-react';

const Appointment = () => {
  const infoCards = [
    {
      icon: <AlertCircle className="h-5 w-5" />,
      title: 'Before Your Visit',
      items: [
        'Please arrive 15 minutes early',
        'Bring your insurance card',
        'Bring a list of current medications',
        'Bring any relevant dental records'
      ]
    },
    {
      icon: <Clock className="h-5 w-5" />,
      title: 'Office Hours',
      items: [
        'Monday - Thursday: 10:30 AM - 8:00 PM',
        'Saturday - Sunday: 10:30 AM - 8:00 PM',
        'Friday: Closed'
      ]
    },
  ];

  return (
    <Layout>
      {/* Hero Section */}
      <div className="hero-gradient text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold sm:text-5xl md:text-6xl">
              Book an Appointment
            </h1>
            <p className="mt-6 max-w-2xl mx-auto text-xl text-blue-100">
              Schedule your visit with our experienced dental professionals
            </p>
          </motion.div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Form Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-2"
          >
            <AppointmentForm />
          </motion.div>

          {/* Info Section */}
          <div className="space-y-6">
            {infoCards.map((card, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.15 }}
                className="bg-white p-6 rounded-xl shadow-md border border-gray-100 border-l-4 border-l-blue-500 hover:shadow-lg transition-shadow duration-300"
              >
                <div className="flex items-center mb-4">
                  <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-blue-100 text-blue-600 mr-3">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{card.title}</h3>
                </div>
                <ul className="space-y-2 text-sm text-gray-500">
                  {card.items.map((item, j) => (
                    <li key={j} className={item === 'Friday: Closed' ? 'text-red-400 font-medium' : ''}>
                      • {item}
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}

            {/* Emergency */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="bg-gradient-to-br from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200"
            >
              <div className="flex items-center mb-3">
                <div className="flex items-center justify-center h-9 w-9 rounded-lg bg-blue-600 text-white mr-3">
                  <Phone className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-bold text-gray-900">Emergency Care</h3>
              </div>
              <p className="text-gray-600 text-sm">
                For dental emergencies during regular hours, call:
              </p>
              <a href="tel:+923147776565" className="mt-2 inline-block text-blue-600 font-bold text-lg hover:text-blue-700 transition-colors">
                +92 314 7776565
              </a>
            </motion.div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Appointment;